import classNames from "classnames";
import {formatInTimeZone} from "date-fns-tz";
import {FiMoon, FiSun} from "react-icons/fi";

import {AutoRefreshButton} from "@/components/autoRefreshButton/AutoRefreshButton.tsx";
import {IconButton} from "@/components/iconButton/IconButton.tsx";
import {ToggleFullscreenButton} from "@/components/toggleFullscreenButton/ToggleFullscreenButton.tsx";

import {SubscribeDropdown} from "../../components/subscribeDropdown/SubscribeDropdown.tsx";
import {useAutoRefresh} from "../../hooks/useAutoRefresh.ts";
import type {PublicStatusPage} from "../../types.ts";
import {InspireGlobalStatus} from "./InspireGlobalStatus.tsx";
import {computeGlobalStatus, formatGlobalStatusDuration} from "./utils.ts";

import styles from "./InspireHeader.module.scss";

interface InspireHeaderProps {
  statusPage: PublicStatusPage;
  slug: string;
  isDark: boolean;
  onToggleDarkMode: () => void;
  onRefresh: () => void;
  autoRefreshPageKey: string | undefined;
  // Sanitized server-side on save; rendered in place of the logo & title.
  customHeaderHtml?: string;
  showOverallStatus?: boolean;
}

export const InspireHeader = ({
  statusPage,
  slug,
  isDark,
  onToggleDarkMode,
  onRefresh,
  autoRefreshPageKey,
  showOverallStatus = true,
  customHeaderHtml,
}: InspireHeaderProps) => {
  const {interval, setAutoRefreshInterval, refreshNow} = useAutoRefresh({
    onRefresh,
    pageKey: autoRefreshPageKey,
  });

  const {status, durationSecs} = computeGlobalStatus(statusPage);
  // A page can be non-operational for a reason the card has no wording for, in which case
  // computeGlobalStatus returns no status and the header must not reserve room for a card.
  const showStatusCard = showOverallStatus && status !== undefined;
  // Drives both the card's second row and the height the header reserves for it, so the two cannot
  // disagree: without a duration the card stays one row tall even below the stack breakpoint.
  const durationText = formatGlobalStatusDuration(status, durationSecs);

  const timezoneOffset = formatInTimeZone(new Date(), statusPage.used_timezone, "xxx");
  const customBg = statusPage.custom_header_bg_color_hex;
  const customText = statusPage.custom_header_text_color_hex;

  return (
    <>
      <div
        data-testid="status-page-header"
        className={styles.header}
        style={
          {
            ...(customBg ? {backgroundColor: customBg, "--surface-text": customBg} : {}),
            ...(customText ? {color: customText, "--surface-bg": customText} : {}),
          } as React.CSSProperties
        }
      >
        <div
          className={classNames(styles.headerContainer, {
            [styles.hasStatusCard]: showStatusCard,
            [styles.hasDurationRow]: showStatusCard && durationText !== null,
          })}
        >
          {customHeaderHtml && (
            // Matches the legacy Django INSPIRE theme: the custom header is a full-width banner
            // that replaces the logo/title/description/timezone block (not squeezed beside the toolbar).
            <div id="inspire-custom-header" dangerouslySetInnerHTML={{__html: customHeaderHtml}} />
          )}
          <div className={styles.topRow}>
            <div className={styles.left}>
              {!customHeaderHtml && (
                <>
                  {statusPage.logo_url && (
                    <div className={`${styles.logoWrapper} inspire-logo-container`}>
                      {statusPage.company_website_url ? (
                        <a href={statusPage.company_website_url} target="_blank" rel="noopener noreferrer">
                          <img src={statusPage.logo_url} alt={statusPage.name} className={styles.logo} />
                        </a>
                      ) : (
                        <img src={statusPage.logo_url} alt={statusPage.name} className={styles.logo} />
                      )}
                    </div>
                  )}
                  <h1 className={styles.title}>{statusPage.name}</h1>
                  {statusPage.description_html && (
                    <div
                      className={`${styles.description} inspire-description`}
                      dangerouslySetInnerHTML={{__html: statusPage.description_html}}
                    />
                  )}
                  <div className={styles.timezone}>
                    Timezone: (GMT {timezoneOffset}) {statusPage.used_timezone}
                  </div>
                </>
              )}
            </div>

            <div className={styles.toolbar}>
              <div className={styles.iconGroup}>
                <ToggleFullscreenButton size="fitIcon" variant="ghostUnderline" />
                <IconButton
                  icon={isDark ? FiSun : FiMoon}
                  variant="ghostUnderline"
                  size="fitIcon"
                  onClick={onToggleDarkMode}
                  title={isDark ? "Switch to light mode" : "Switch to dark mode"}
                  aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
                />
                <AutoRefreshButton
                  seconds={interval > 0 ? interval * 60 : undefined}
                  setSeconds={(s) => setAutoRefreshInterval(s ? ((s / 60) as typeof interval) : 0)}
                  doRefresh={refreshNow}
                  size="fitIcon"
                  variant="ghostUnderline"
                />
              </div>
              {statusPage.allow_subscriptions && (
                <SubscribeDropdown
                  slug={slug}
                  allowEmail={statusPage.allow_subscriptions_email}
                  allowSms={statusPage.allow_subscriptions_sms}
                  allowRss={statusPage.allow_subscriptions_rss}
                  allowSlack={statusPage.allow_subscriptions_slack}
                  allowWebhook={statusPage.allow_subscriptions_webhook}
                  slackOAuthUrl={statusPage.slack_oauth_url ?? null}
                  variant="surfaceAdaptive"
                  usePortal
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {showStatusCard && (
        <div className={classNames(styles.statusCardRow, {[styles.hasDurationRow]: durationText !== null})}>
          <InspireGlobalStatus status={status} durationText={durationText} />
        </div>
      )}
    </>
  );
};
